import styled from "@emotion/styled";
import { TextField } from "../shared/components/TextField";
import {
  USER_PROFILE_SCHEMA_DEFAULT_VALUES,
  UserProfileSchema,
  userProfileFormSchema,
} from "../features/Dashboard/user-profile-form-helpers";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Chip, Grid, Tooltip, Typography } from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { UserRoles, useAuthStore } from "../store/auth.store";
import { PageLayout } from "../shared/layouts/PageLayout";
import { Form } from "../shared/components/Form";
import { useEditUserProfile } from "../queries/profile/useEditUserProfile";
import { dashboardBreadcrumbs } from "../features/Dashboard/dashboard-breadcrumbs";
import { CountrySelect } from "@shared/components/CountrySelect";
import { StateField } from "@shared/components/StateField";
import { getBrowserDefaultCountry } from "@shared/utils/countries";
import MaskedInput from "react-text-mask";
import { strToNumericStr } from "@shared/utils/form-converters";
import { phoneNumberMask } from "@shared/utils/input-masks";
import { useUpdateUserProfileAvatar } from "@queries/profile/useUpdateUserProfileAvatar";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { ImageUploadWithPreview } from "@features/UserProfile/ImageUploadWithPreview";
import { useRemoveUserProfileAvatar } from "@queries/profile/useRemoveUserProfileAvatar";
import { RoleAuthorized } from "@shared/components/RoleAuthorized";
import { UserBranding } from "@features/UserProfile/UserBranding";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useUpdateUserCommunicationPreferences } from "@queries/profile/useUpdateUserCommunicationPreferences";
import { dashboardRoutePaths } from "@routes/dashboard.routes";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";

type SettingsProfileLocationState = {
  smsChatEnabled?: boolean;
};

const breadcrumbs = {
  current: dashboardBreadcrumbs.profile,
  previous: [dashboardBreadcrumbs.accountSettings],
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-radius: 1rem;
  color: ${(p) => p.theme.text.colors.primary};
  background-color: ${(p) => p.theme.colors.background};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2);
  width: 100%;

  &[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const getUserMainRole = (roles: UserRoles[]) => {
  if (roles.includes(UserRoles.Admin)) {
    return "Admin";
  }
  if (roles.includes(UserRoles.User)) {
    return "Basic";
  }
  return "Basic";
};

const getAllowedMLSProviders = (allowedMlsProviders: string[]) => {
  if (allowedMlsProviders.length === 0) return "None";

  if (allowedMlsProviders.includes("*")) return "All MLSs";

  return allowedMlsProviders.filter((p) => p !== "*").join(", ");
};

export function SettingsProfile() {
  const user = useAuthStore((state) => state.user);
  const setPartialUserData = useAuthStore((state) => state.setPartialUserData);
  const hasMlsAccessModule = useHasModule(sidekickModuleEnum.MLS_ACCESS);
  const [searchParams] = useSearchParams();
  const location = useLocation() as {
    state: SettingsProfileLocationState | null;
  };

  const fieldFocus = searchParams.get("field_focus");
  const shouldFocusPhoneNumber = fieldFocus === "phoneNumber";
  const navigate = useNavigate();

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
  } = useForm<UserProfileSchema>({
    defaultValues: {
      name: user?.name ?? USER_PROFILE_SCHEMA_DEFAULT_VALUES.name,
      countryState:
        user?.countryState ?? USER_PROFILE_SCHEMA_DEFAULT_VALUES.countryState,
      country: user?.country || getBrowserDefaultCountry(),
      phoneNumber:
        user?.phoneNumber ?? USER_PROFILE_SCHEMA_DEFAULT_VALUES.phoneNumber,
      officeName:
        user?.officeName ?? USER_PROFILE_SCHEMA_DEFAULT_VALUES.officeName,
    },
    resolver: yupResolver(userProfileFormSchema),
    reValidateMode: "onChange",
  });
  const selectedCountry = watch("country");

  const userProfileAvatarMutation = useUpdateUserProfileAvatar({
    onSuccess: (url) => {
      setPartialUserData({ avatar: url });
    },
  });
  const removeProfilePhotoMutation = useRemoveUserProfileAvatar({
    onSuccess: () => {
      setPartialUserData({ avatar: null });
    },
  });

  const editUserProfileMutation = useEditUserProfile({
    onSuccess: (data) => {
      setPartialUserData({
        name: data.name,
        firstName: data.name.split(" ")[0],
        countryState: data.countryState,
        country: data.country,
        phoneNumber: data.phoneNumber,
        timezone: data.timezone,
        officeName: data.officeName,
      });

      const smsChatEnabled = location.state?.smsChatEnabled;
      if (data.phoneNumber && smsChatEnabled) {
        updateUserCommunicationPreferencesMutation.mutate({
          type: "smsChat",
          enabled: true,
        });
      }
    },
  });

  const updateUserCommunicationPreferencesMutation =
    useUpdateUserCommunicationPreferences({
      onSettled: () => {
        setTimeout(() => {
          navigate(dashboardRoutePaths.connectedApps.originalPath, {
            state: null,
          });
        }, 500);
      },
    });

  const onSubmit = (data: UserProfileSchema) => {
    if (!isDirty) return;
    editUserProfileMutation.mutate({
      ...data,
      phoneNumber: strToNumericStr(data.phoneNumber ?? ""),
      website: data.website || null,
    });
    reset(data);
  };

  const mlsProvidersLabels =
    getAllowedMLSProviders(user?.allowedMlsProvidersLabels || []) || "";

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <Container>
        <Form onSubmit={handleSubmit(onSubmit)} maxWidth="100%">
          <Box>
            <Grid
              container
              rowSpacing={3}
              columnSpacing={{ xs: 3, md: 6 }}
              alignItems={"flex-start"}
            >
              <Grid item xs={12} md={3} order={{ xs: 1, md: 3 }}>
                <ImageUploadWithPreview
                  label="Profile Photo"
                  imageSrc={user.avatar}
                  alt={user.name ?? ""}
                  onSave={userProfileAvatarMutation.mutateAsync}
                  onRemove={removeProfilePhotoMutation.mutate}
                  isLoading={
                    userProfileAvatarMutation.isPending ||
                    removeProfilePhotoMutation.isPending
                  }
                  aspectRatio={1}
                  placeholderIcon={<CameraAltOutlinedIcon />}
                />
              </Grid>

              <Grid
                container
                item
                columnSpacing={3}
                rowSpacing={1}
                xs={12}
                md={12}
                order={{ xs: 2, md: 1 }}
              >
                <Grid item xs={12} md={12}>
                  <Typography variant="h6" sx={{ marginBottom: 1 }}>
                    Personal Information{" "}
                    {getUserMainRole(user.roles) === "Admin" && (
                      <Chip
                        component={"span"}
                        label="Admin"
                        icon={<AdminPanelSettingsIcon />}
                        size="small"
                        sx={{ marginLeft: 1 }}
                      />
                    )}
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                container
                item
                columnSpacing={3}
                rowSpacing={1}
                xs={12}
                md={9}
                order={{ xs: 3, md: 2 }}
              >
                <Grid item xs={12} md={6}>
                  <TextField
                    name="name"
                    label="Full Name"
                    inputProps={{ ...register("name") }}
                    fieldError={errors.name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="email"
                    label="E-mail"
                    defaultValue={user?.email ?? ""}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                {hasMlsAccessModule && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="mlsId"
                      label="License number"
                      defaultValue={user?.mlsId ?? ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    name="officeName"
                    label="Brokerage name"
                    defaultValue={user?.officeName ?? ""}
                    inputProps={{ ...register("officeName") }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MaskedInput
                    mask={phoneNumberMask}
                    render={(ref, props) => (
                      <TextField
                        InputProps={{ inputRef: ref }}
                        {...props}
                        label="Phone number"
                        name="phoneNumber"
                        defaultValue={user?.phoneNumber ?? ""}
                        inputProps={{ ...register("phoneNumber") }}
                        fieldError={errors.phoneNumber}
                        autoFocus={shouldFocusPhoneNumber}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={2}>
                    <Box flex={1}>
                      <Controller
                        name="countryState"
                        control={control}
                        render={({ field }) => (
                          <StateField
                            country={selectedCountry}
                            name="countryState"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            fieldError={errors.countryState}
                          />
                        )}
                      />
                    </Box>
                    <Box flex={1}>
                      <Controller
                        name="country"
                        control={control}
                        render={({ field }) => (
                          <CountrySelect
                            label="Country"
                            name="country"
                            value={field.value}
                            onChange={(newCountry) => {
                              field.onChange(newCountry);
                              setValue("countryState", "");
                            }}
                            onBlur={field.onBlur}
                            fieldError={errors.country}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Grid>

                {hasMlsAccessModule && (
                  <Grid item xs={12} md={12}>
                    <Tooltip title={mlsProvidersLabels}>
                      <TextField
                        name="allowedMls"
                        label="Allowed MLS Providers"
                        value={mlsProvidersLabels}
                        InputProps={{ readOnly: true }}
                      />
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Box>

          <SubmitButton
            sx={{ justifySelf: "flex-start", gap: 1.5, minWidth: "160px" }}
            isLoading={editUserProfileMutation.isPending}
            disabled={!isDirty}
            loadingText="Saving..."
          >
            Save
          </SubmitButton>
        </Form>

        {hasMlsAccessModule && (
          <RoleAuthorized
            userRoles={[user.subscriptionPlanTier]}
            authorizedRoles={["pro", "basic"]}
            authorizedComponent={<UserBranding />}
          />
        )}
      </Container>
    </PageLayout>
  );
}
